import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import type { AuditFilters } from '@/features/audit/api'
import { AuditScreen } from '@/features/audit/audit-screen'
import { presetRange } from '@/features/audit/audit-utils'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the company audit screen — the company's own trail
 * (FR-AUD-003) and the support sessions opened into its group (FR-AUD-005).
 *
 * The grant gate is presentation only — the API enforces `audit.view` on
 * both endpoints; this only keeps a user without the grant out of a screen
 * they cannot use.
 *
 * Server-side filters live in URL search params so a filtered trail is linkable.
 */
export const Route = createFileRoute('/_authenticated/audit')({
  validateSearch: (search: Record<string, unknown>): AuditFilters => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    actor_id: typeof search.actor_id === 'string' ? search.actor_id : undefined,
    support_session_id:
      typeof search.support_session_id === 'string' ? search.support_session_id : undefined,
    module: typeof search.module === 'string' ? search.module : undefined,
    entity_type: typeof search.entity_type === 'string' ? search.entity_type : undefined,
    entity_id: typeof search.entity_id === 'string' ? search.entity_id : undefined,
    action_kind: ['created', 'changed', 'deleted', 'other'].includes(String(search.action_kind))
      ? (search.action_kind as AuditFilters['action_kind'])
      : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'audit.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: AuditRoute,
})

function AuditRoute() {
  const search = Route.useSearch()
  const params = { ...presetRange('30days'), ...search }
  const navigate = useNavigate()
  return (
    <AuditScreen
      params={params}
      onParamsChange={(next) => void navigate({ to: '/audit', search: next, replace: true })}
    />
  )
}
